// ProductSalesChart.js
// Chart showing sales value over time broken down by product

// Imports
import React from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Tooltip, Button, Position, Checkbox } from '@blueprintjs/core';
import { MultiSelect } from '@blueprintjs/select';
import TimeSeriesChart, { aggregateByDay, processData } from './TimeSeriesChart';
import { ValueType, ChartMode, CHART_COLORS } from './chartEnums';

// Returns the total value of an archived order for a specific product
export function getProductArchivedTotal(order, productId) {
  let total = 0;
  for (const clientId in order.byClient) {
    const clientData = order.byClient[clientId];
    const product = clientData[productId];
    if (product && product.price !== undefined) {
      total += product.price;
    }
  }
  return total;
}

// Returns the total value of the open order for a specific product
export function getProductOpenTotal(currentOrder, products, productId) {
  if (!currentOrder || !products || !products.byId || !products.byId[productId]) return 0;
  let total = 0;
  const prod = products.byId[productId];

  for (const clientId in currentOrder.byClient) {
    const clientData = currentOrder.byClient[clientId];
    const productOrder = clientData[productId];
    if (productOrder) {
      const unitIdx = productOrder.unitId >= prod.price.length ? prod.price.length - 1 : productOrder.unitId;
      const price = productOrder.customPrice ? productOrder.customPrice : prod.price[unitIdx].price * productOrder.quantity;
      total += price;
    }
  }
  return total;
}

// Converts orders into dataPoints for a single product
export function convertOrdersToProductDataPoints(orders, products, productId, includeOpen = true) {
  const dataPoints = [];

  orders.archive.forEach(order => {
    if (order.dateClosed) {
      const total = getProductArchivedTotal(order, productId);
      if (total > 0) {
        dataPoints.push({
          x: new Date(order.dateClosed),
          y: total,
        });
      }
    }
  });

  if (includeOpen && orders.current) {
    const total = getProductOpenTotal(orders.current, products, productId);
    if (total > 0) {
      dataPoints.push({
        x: new Date(),
        y: total,
      });
    }
  }

  return dataPoints;
}

// Returns a Set of product IDs that have at least one sale
export function getProductsWithSales(orders) {
  const productsWithSales = new Set();
  
  orders.archive.forEach(order => {
    for (const clientId in order.byClient) {
      const clientData = order.byClient[clientId];
      Object.keys(clientData).forEach(key => {
        if (key !== 'adjustment') {
          productsWithSales.add(key);
        }
      });
    }
  });
  
  if (orders.current) {
    for (const clientId in orders.current.byClient) {
      const clientData = orders.current.byClient[clientId];
      Object.keys(clientData).forEach(key => {
        if (key !== 'adjustment') {
          productsWithSales.add(key);
        }
      });
    }
  }
  
  return productsWithSales;
}

// Returns the top N product IDs by sales volume in the relevant time interval for the current mode
export function getTopProductsByMode(orders, products, mode, ytdYear, n, includeOpen = true) {
  const pointsByProduct = {};
  const allProductIds = getProductsWithSales(orders);

  // Convert points for all active products
  allProductIds.forEach(id => {
    pointsByProduct[id] = convertOrdersToProductDataPoints(orders, products, id, includeOpen);
  });

  // Find the exact global years span to compute the top products correctly for Yearly mode
  let globalYears = null;
  if (mode === ChartMode.YEARLY) {
    const yearsSet = new Set();
    allProductIds.forEach(id => {
      pointsByProduct[id].forEach(pt => yearsSet.add(new Date(pt.x).getFullYear()));
    });
    globalYears = Array.from(yearsSet).map(Number).sort((a,b) => a-b);
  }

  // Calculate the total relevant value for each product based on mode
  const totals = Array.from(allProductIds).map(id => {
    const daily = aggregateByDay(pointsByProduct[id]);
    const chartData = processData(daily, mode, undefined, ytdYear, globalYears);

    // Sum the values of the final interval bucket
    let relevantTotal = 0;
    if (chartData.values && chartData.values.length > 0) {
      if (mode === ChartMode.YEARLY) {
        // Top products in the most recent year overall
        const targetYear = globalYears.length > 0
          ? Math.max(globalYears[globalYears.length - 1], new Date().getFullYear())
          : new Date().getFullYear();
        const yearIndex = chartData.labels.indexOf(String(targetYear));
        if (yearIndex >= 0) relevantTotal = chartData.values[yearIndex];
      } else if (mode === ChartMode.YTD) {
        // Top products in the last month of target year
        relevantTotal = chartData.values[chartData.values.length - 1];
      } else if (mode === ChartMode.YTD_ACCUMULATED) {
        // Top products in the YTD_ACCUMULATED total (sum of all months in the target year)
        relevantTotal = chartData.values.reduce((sum, v) => sum + v, 0);
      } else if (mode === ChartMode.WEEKLY) {
        // Top products in the last 10 weeks combined
        relevantTotal = chartData.values.reduce((sum, v) => sum + v, 0);
      }
    }

    return { id, total: relevantTotal };
  });

  // Sort descending and take top N
  totals.sort((a, b) => b.total - a.total);
  return totals.slice(0, n).map(p => p.id);
}

// Component Class
class ProductSalesChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedProductIds: [],
      currentMode: ChartMode.YTD,
      currentYtdYear: null,
      includeOpen: true,
    };
  }

  // Returns name for the given product ID
  getProductName = (id) => {
    if (!id || !this.props.products.byId[id]) return '';
    return this.props.products.byId[id].name;
  }

  // MultiSelect tag renderer
  renderTag = (id) => {
    return this.getProductName(id);
  }

  // Clears a product from the selection when tag X is clicked
  handleTagRemove = (_tagLabel, index) => {
    this.setState(s => ({
      selectedProductIds: s.selectedProductIds.filter((_, i) => i !== index),
    }));
  }

  // Handles selecting/deselecting a product from the dropdown
  handleProductSelect = (id) => {
    this.setState(s => {
      const selected = s.selectedProductIds;
      if (selected.includes(id)) {
        return { selectedProductIds: selected.filter(selId => selId !== id) };
      } else {
        return { selectedProductIds: [...selected, id] };
      }
    });
  }

  // Predicate for filtering the dropdown via text
  filterProduct = (query, id) => {
    const name = this.getProductName(id);
    return name.toLowerCase().includes(query.toLowerCase());
  }

  // Renders a product in the dropdown
  renderProductItem = (id, options) => {
    if (!options.modifiers.matchesPredicate) return null;
    const isSelected = this.state.selectedProductIds.includes(id);

    return (
      <MenuItem
        key={id}
        active={options.modifiers.active}
        icon={isSelected ? "tick" : "blank"}
        text={this.getProductName(id)}
        onClick={options.handleClick}
        shouldDismissPopover={false}
      />
    );
  }

  // Triggered by TimeSeriesChart when mode or year changes
  handleChartModeChange = (mode, ytdYear) => {
    this.setState({ currentMode: mode, currentYtdYear: ytdYear });
  }

  handleToggleOpen = (e) => {
    this.setState({ includeOpen: e.target.checked });
  }

  // Renders Component
  render() {
    const { orders, products } = this.props;
    const { selectedProductIds, currentMode, currentYtdYear, includeOpen } = this.state;

    // Filter available products dropdown to only show products that have sales
    // and sort them alphabetically
    const productsWithSales = getProductsWithSales(orders);
    const availableProducts = products.ids
      .map(String)
      .filter(id => productsWithSales.has(id))
      .sort((a, b) => this.getProductName(a).localeCompare(this.getProductName(b)));

    // Determine the active list of products to graph
    let activeProductIds = selectedProductIds;
    if (activeProductIds.length === 0) {
      // Auto-fallback: top 5 by sales volume
      activeProductIds = getTopProductsByMode(orders, products, currentMode, currentYtdYear, 5, includeOpen);
    }

    // Build the Chart.js datasets array
    const datasets = activeProductIds.map((productId, index) => ({
      label: this.getProductName(productId) || `Produto ${productId}`,
      dataPoints: convertOrdersToProductDataPoints(orders, products, productId, includeOpen),
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));

    var info = (
      <div>
        É possível selecionar quantos produtos forem necessários.<br />
        Se nenhum produto for selecionado, serão exibidos os 5 produtos com maior volume de vendas.<br />
      </div>
    );

    return (
      <div>
        <h3 style={{ textAlign: 'center' }} className="pt-heading">Vendas por Produto</h3>
        <div style={{ marginBottom: '20px' }}>
          <div className="pt-label" style={{ marginBottom: '8px' }}>
            <Tooltip content={info} position={Position.RIGHT}>
              <Button rightIcon="info-sign" small minimal>
                <b>Selecione um ou mais Produtos</b>
              </Button>
            </Tooltip>
          </div>
          <MultiSelect
            items={availableProducts}
            itemPredicate={this.filterProduct}
            itemRenderer={this.renderProductItem}
            onItemSelect={this.handleProductSelect}
            tagRenderer={this.renderTag}
            tagInputProps={{
              onRemove: this.handleTagRemove,
              inputProps: {
                placeholder: 'Pesquisar...',
              },
            }}
            selectedItems={selectedProductIds}
            popoverProps={{ minimal: true }}
            resetOnSelect={true}
          />
        </div>
        <Checkbox 
          label="Incluir pedidos em aberto" 
          checked={includeOpen} 
          onChange={this.handleToggleOpen} 
          style={{ marginBottom: '10px' }}
        />
        <TimeSeriesChart
          datasets={datasets}
          valueType={ValueType.CURRENCY}
          onModeChange={this.handleChartModeChange}
          fillArea={false}
        />
      </div>
    );
  }
}

// Required Properties
ProductSalesChart.propTypes = {
  orders: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
};

// Exports
export default ProductSalesChart;



// WEBPACK FOOTER //
// ./src/components/ChartView/ProductSalesChart.js